import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Flag, BarChart3, Pause, CheckCircle, Check } from "lucide-react";
import { useCourseData } from "@/hooks/useCourseData";

const UNIT_TYPES = [
  { value: "birdie", label: "Birdie", defaultValue: 1 },
  { value: "aguila", label: "Águila", defaultValue: 2 },
  { value: "sandybirdie", label: "Sandybirdie", defaultValue: 2 },
  { value: "sandypar", label: "Sandypar", defaultValue: 1 },
  { value: "par_mojado", label: "Par mojado", defaultValue: 1 },
  { value: "par_ob", label: "Par OB", defaultValue: 1 },
  { value: "birdie_mamada", label: "Birdie mamada", defaultValue: 2 },
  { value: "par_mamada", label: "Par mamada", defaultValue: 1 },
  { value: "otro", label: "Otro", defaultValue: 1 },
];

export default function HoleCapture() {
  const navigate = useNavigate();
  const { roundId } = useParams();
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentHole, setCurrentHole] = useState(1);
  const [scores, setScores] = useState({});
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [exitLoading, setExitLoading] = useState(false);

  // Oyes state (for par 3 holes)
  const [oyesFirstPlace, setOyesFirstPlace] = useState("");
  const [oyesSecondPlace, setOyesSecondPlace] = useState("");

  // Course data from API
  const { coursesData, coursesLoading } = useCourseData();
  const coursesRef = useRef(null);
  useEffect(() => { coursesRef.current = coursesData; }, [coursesData]);

  const courseName = round?.course || "Guadalajara Country Club";
  const HOLES = coursesData?.[courseName] || [];
  const PAR_3_HOLES = HOLES.filter(h => h.par === 3).map(h => h.hole);

  const getOrderedHoles = (startingHole) => {
    if (startingHole === 10) {
      return [...HOLES.slice(9), ...HOLES.slice(0, 9)];
    }
    return HOLES;
  };

  const getNextHole = (hole, startingHole) => {
    if (startingHole === 10) {
      if (hole === 18) return 1;
      if (hole === 9) return null;
      return hole + 1;
    }
    if (hole === 18) return null;
    return hole + 1;
  };

  const getPrevHole = (hole, startingHole) => {
    if (startingHole === 10) {
      if (hole === 10) return null;
      if (hole === 1) return 18;
      return hole - 1;
    }
    if (hole === 1) return null;
    return hole - 1;
  };

  const isLastHole = (hole, startingHole) => {
    if (startingHole === 10) return hole === 9;
    return hole === 18;
  };

  const fetchRound = useCallback(async () => {
    try {
      const response = await fetch(`${API}/rounds/${roundId}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setRound(data);
        const startHole = data.starting_hole || 1;
        setCurrentHole(data.current_hole || startHole);

        const courseHoles = coursesRef.current?.[data.course] || [];
        const existingScores = {};
        const holeToLoad = data.current_hole || startHole;
        data.players?.forEach(player => {
          const holeScore = player.scores?.find(s => s.hole_number === holeToLoad);
          existingScores[player.player_id] = {
            gross_score: holeScore?.gross_score || (courseHoles[holeToLoad - 1]?.par || 4),
            has_unit: holeScore?.has_unit || false,
            unit_type: holeScore?.unit_type || "",
            unit_count: holeScore?.unit_count || 0,
          };
        });
        setScores(existingScores);

        const oyesResult = data.oyes_results?.[String(holeToLoad)];
        if (oyesResult) {
          setOyesFirstPlace(oyesResult.first_place?.player_id || "");
          setOyesSecondPlace(oyesResult.second_place?.player_id || "");
        }
      } else {
        toast.error("Ronda no encontrada");
        navigate("/dashboard");
      }
    } catch {
      toast.error("Error al cargar la ronda");
    } finally {
      setLoading(false);
    }
  }, [roundId, navigate]);

  useEffect(() => { fetchRound(); }, [fetchRound]);

  const changeHole = (newHole) => {
    if (newHole < 1 || newHole > 18) return;
    setCurrentHole(newHole);
    setOyesFirstPlace("");
    setOyesSecondPlace("");

    const existingScores = {};
    round?.players?.forEach(player => {
      const holeScore = player.scores?.find(s => s.hole_number === newHole);
      existingScores[player.player_id] = {
        gross_score: holeScore?.gross_score || (HOLES[newHole - 1]?.par || 4),
        has_unit: holeScore?.has_unit || false,
        unit_type: holeScore?.unit_type || "",
        unit_count: holeScore?.unit_count || 0,
      };
    });
    setScores(existingScores);

    const oyesResult = round?.oyes_results?.[String(newHole)];
    if (oyesResult) {
      setOyesFirstPlace(oyesResult.first_place?.player_id || "");
      setOyesSecondPlace(oyesResult.second_place?.player_id || "");
    }
  };

  const updateScore = (playerId, field, value) => {
    setScores(prev => ({ ...prev, [playerId]: { ...prev[playerId], [field]: value } }));
  };

  const adjustScore = (playerId, delta) => {
    const current = scores[playerId]?.gross_score || currentHoleData.par;
    updateScore(playerId, "gross_score", Math.max(1, current + delta));
  };

  const submitScores = async () => {
    setSubmitting(true);
    try {
      const scoresArray = Object.entries(scores).map(([playerId, data]) => ({
        player_id: playerId,
        gross_score: data.gross_score,
        has_unit: data.has_unit,
        unit_type: data.has_unit ? data.unit_type : null,
        unit_count: data.has_unit ? (data.unit_count || 1) : 0,
      }));

      const requestBody = { hole_number: currentHole, scores: scoresArray };
      if (PAR_3_HOLES.includes(currentHole) && oyesFirstPlace && oyesSecondPlace) {
        requestBody.oyes_first_place = oyesFirstPlace;
        requestBody.oyes_second_place = oyesSecondPlace;
      }

      const response = await fetch(`${API}/rounds/${roundId}/scores`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        const updatedRound = await response.json();
        setRound(updatedRound);

        if (updatedRound.status === "awaiting_doubles") {
          toast.success("¡Primera vuelta completada!");
          navigate(`/round/${roundId}/doubles`);
          return;
        }

        const startingHole = round?.starting_hole || 1;
        const nextHole = getNextHole(currentHole, startingHole);

        if (nextHole !== null && updatedRound.status !== "completed") {
          toast.success(`Hoyo ${currentHole} guardado`);
          changeHole(nextHole);
        } else {
          toast.success("¡Ronda completada!");
          navigate(`/round/${roundId}/results`);
        }
      } else {
        throw new Error("Error submitting scores");
      }
    } catch {
      toast.error("Error al guardar scores");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseRound = () => {
    toast.success("Ronda pausada");
    navigate("/dashboard");
  };

  const handleFinishRound = async () => {
    setExitLoading(true);
    try {
      const response = await fetch(`${API}/rounds/${roundId}/complete`, {
        method: "POST", credentials: "include",
      });
      if (response.ok) {
        toast.success("Ronda finalizada");
        navigate(`/round/${roundId}/results`);
      } else {
        throw new Error("Error completing round");
      }
    } catch {
      toast.error("Error al finalizar la ronda");
    } finally {
      setExitLoading(false);
      setShowExitDialog(false);
    }
  };

  if (loading || coursesLoading || !HOLES.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F3EA]">
        <div className="w-8 h-8 border-4 border-[#103B31] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const currentHoleData = HOLES[currentHole - 1];
  const basePlayers = round?.players?.filter(p => p.is_base) || [];
  const secondaryPlayers = round?.players?.filter(p => !p.is_base) || [];
  const completedHoles = round?.players?.[0]?.scores?.length || 0;
  const anyDoubled = (round?.matchups || []).some(m => m.is_doubled) || (round?.individual_matchups || []).some(m => m.is_doubled);

  const getScoreColor = (score, par) => {
    const diff = score - par;
    if (diff <= -2) return "text-amber-500 font-black";
    if (diff === -1) return "text-blue-600 font-bold";
    if (diff === 0) return "text-[#102E32]";
    if (diff === 1) return "text-amber-600";
    return "text-red-600 font-bold";
  };

  const prevHole = getPrevHole(currentHole, round?.starting_hole || 1);
  const nextHoleNum = getNextHole(currentHole, round?.starting_hole || 1);

  return (
    <div className="min-h-screen bg-[#F7F3EA] text-[#102E32]" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="hole-capture-page">
      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent className="bg-white border-[#DED5C2] text-[#102E32] max-w-sm" style={{ fontFamily: "Manrope, sans-serif" }}>
          <DialogHeader>
            <DialogTitle>Salir de la Ronda</DialogTitle>
            <DialogDescription className="text-[#8A968C]">
              Has completado {completedHoles} de 18 hoyos. ¿Qué deseas hacer?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <button
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-[#F1EBDD] hover:bg-[#F1EBDD] text-left transition-colors"
              onClick={handlePauseRound}
              disabled={exitLoading}
              data-testid="pause-round-btn"
            >
              <Pause className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold">Pausar Ronda</p>
                <p className="text-xs text-[#9AA69C]">Guardar progreso y continuar después</p>
              </div>
            </button>
            <button
              className="w-full flex items-center gap-3 p-4 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-left transition-colors"
              onClick={handleFinishRound}
              disabled={exitLoading}
              data-testid="finish-round-btn"
            >
              {exitLoading ? (
                <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
              ) : (
                <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              )}
              <div>
                <p className="font-semibold text-red-600">Finalizar Ronda</p>
                <p className="text-xs text-[#9AA69C]">Ver resultados hasta el hoyo {completedHoles}</p>
              </div>
            </button>
          </div>
          <DialogFooter>
            <button className="w-full py-2.5 rounded-xl bg-[#F1EBDD] text-[#4B5A50] font-semibold text-sm" onClick={() => setShowExitDialog(false)}>
              Cancelar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#DED5C2] bg-white/60">
        <button onClick={() => setShowExitDialog(true)} className="text-[#6B7A6F] hover:text-[#102E32]" data-testid="exit-btn">
          <ArrowLeft size={20} />
        </button>
        <span className="text-sm font-medium text-[#3D4A42] truncate max-w-[200px]">{round?.name}</span>
        <div className="flex items-center gap-2">
          {anyDoubled && (
            <span className="text-[10px] font-black text-[#103B31] bg-[#103B31]/10 px-2 py-0.5 rounded-full" data-testid="classic-doubled-badge">x2</span>
          )}
          <button onClick={() => navigate(`/round/${roundId}/summary`)} className="text-[#6B7A6F] hover:text-[#102E32]" data-testid="summary-btn">
            <BarChart3 size={18} className="text-[#103B31]" />
          </button>
        </div>
      </div>

      {/* Hole selector strip */}
      <div className="flex overflow-x-auto gap-1 px-3 py-2 border-b border-[#DED5C2] no-scrollbar">
        {getOrderedHoles(round?.starting_hole || 1).map((hole) => {
          const done = round?.players?.[0]?.scores?.some(s => s.hole_number === hole.hole);
          const active = hole.hole === currentHole;
          return (
            <button key={hole.hole} onClick={() => changeHole(hole.hole)}
              className={`flex-shrink-0 w-9 h-9 rounded-full text-xs font-bold flex items-center justify-center transition-all
                ${active ? "bg-[#103B31] text-white scale-110" : done ? "bg-[#103B31]/10 text-[#103B31]" : "bg-[#F1EBDD] text-[#9AA69C]"}`}
              data-testid={`hole-btn-${hole.hole}`}>
              {done && !active ? <Check size={12} /> : hole.hole}
            </button>
          );
        })}
      </div>

      {/* Current hole info */}
      <div className="text-center py-6 px-4">
        <p className="text-xs uppercase tracking-widest text-[#9AA69C] mb-1">Hoyo</p>
        <p className="text-6xl font-black text-[#102E32] mb-1" data-testid="current-hole-number">{currentHole}</p>
        <div className="flex items-center justify-center gap-4 text-sm text-[#6B7A6F]">
          <span>Par {currentHoleData.par}</span>
          <span>HCP {currentHoleData.handicap_index}</span>
        </div>
      </div>

      {/* Base players */}
      <div className="px-4 space-y-3">
        <p className="text-[10px] uppercase tracking-widest text-[#103B31] font-bold flex items-center gap-2">
          <span className="w-2 h-2 bg-[#7ED957] rounded-full"></span> Jugadores Base
        </p>
        {basePlayers.map((player) => (
          <PlayerScoreCard key={player.player_id} player={player} score={scores[player.player_id]}
            par={currentHoleData.par}
            onAdjustScore={(delta) => adjustScore(player.player_id, delta)}
            onUpdateScore={(field, value) => updateScore(player.player_id, field, value)}
            getScoreColor={getScoreColor} />
        ))}

        <p className="text-[10px] uppercase tracking-widest text-[#E6B733] font-bold flex items-center gap-2 pt-2">
          <span className="w-2 h-2 bg-[#E6B733] rounded-full"></span> Jugadores Secundarios
        </p>
        {secondaryPlayers.map((player) => (
          <PlayerScoreCard key={player.player_id} player={player} score={scores[player.player_id]}
            par={currentHoleData.par}
            onAdjustScore={(delta) => adjustScore(player.player_id, delta)}
            onUpdateScore={(field, value) => updateScore(player.player_id, field, value)}
            getScoreColor={getScoreColor} />
        ))}
      </div>

      {/* Oyes section - Only for Par 3 holes */}
      {PAR_3_HOLES.includes(currentHole) && (
        <div className="px-4 mt-4" data-testid="oyes-section">
          <div className="bg-[#E6B733]/10 border border-[#E6B733]/25 rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <Flag className="w-4 h-4 text-[#E6B733]" />
              <h3 className="font-bold text-[#102E32]">Oyes</h3>
              <span className="text-[10px] bg-[#E6B733]/20 text-[#E6B733] px-2 py-0.5 rounded-full font-bold">Par 3</span>
            </div>
            <p className="text-xs text-[#9AA69C] mb-4">Jugadores más cercanos a la bandera (1° y 2° lugar)</p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#9AA69C] block mb-1.5">1° Lugar</label>
                <Select value={oyesFirstPlace}
                  onValueChange={(value) => {
                    setOyesFirstPlace(value);
                    if (value === oyesSecondPlace) setOyesSecondPlace("");
                  }}>
                  <SelectTrigger className="w-full bg-white border-[#DED5C2] text-[#102E32]" data-testid="oyes-first-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-[#102E32] border-[#DED5C2]">
                    {round?.players?.map((player) => (
                      <SelectItem key={player.player_id} value={player.player_id}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${player.is_base ? 'bg-[#7ED957]' : 'bg-[#E6B733]'}`}></span>
                          {player.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#9AA69C] block mb-1.5">2° Lugar</label>
                <Select value={oyesSecondPlace} onValueChange={setOyesSecondPlace}>
                  <SelectTrigger className="w-full bg-white border-[#DED5C2] text-[#102E32]" data-testid="oyes-second-select">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white text-[#102E32] border-[#DED5C2]">
                    {round?.players?.filter(p => p.player_id !== oyesFirstPlace).map((player) => (
                      <SelectItem key={player.player_id} value={player.player_id}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${player.is_base ? 'bg-[#7ED957]' : 'bg-[#E6B733]'}`}></span>
                          {player.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {oyesFirstPlace && oyesSecondPlace && (
              <div className="mt-4 pt-3 border-t border-[#E6B733]/20">
                <OyesPreview players={round?.players || []} firstPlace={oyesFirstPlace} secondPlace={oyesSecondPlace} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Save + Navigate */}
      <div className="px-4 py-6 pb-10 space-y-3">
        <button onClick={submitScores} disabled={submitting}
          className="w-full py-4 rounded-xl bg-[#103B31] hover:bg-[#092B25] text-white font-bold text-base flex items-center justify-center gap-2 disabled:opacity-50 transition-all active:scale-[0.98]"
          data-testid="save-hole-btn">
          {submitting ? "Guardando..." : isLastHole(currentHole, round?.starting_hole || 1) ? (
            <><Flag size={16} /> Finalizar Ronda</>
          ) : (
            <><Check size={16} /> Guardar Hoyo {currentHole}</>
          )}
        </button>

        <div className="flex gap-2">
          {prevHole !== null && (
            <button onClick={() => changeHole(prevHole)}
              className="flex-1 py-2.5 rounded-lg bg-[#F1EBDD] text-[#6B7A6F] text-sm font-medium flex items-center justify-center gap-1"
              data-testid="prev-hole-btn">
              <ArrowLeft size={14} /> Hoyo {prevHole}
            </button>
          )}
          {nextHoleNum !== null && (
            <button onClick={() => changeHole(nextHoleNum)}
              className="flex-1 py-2.5 rounded-lg bg-[#F1EBDD] text-[#6B7A6F] text-sm font-medium flex items-center justify-center gap-1"
              data-testid="next-hole-btn">
              Hoyo {nextHoleNum} <ArrowRight size={14} />
            </button>
          )}
        </div>

        <button onClick={() => navigate(`/round/${roundId}/summary`)}
          className="w-full py-3 rounded-xl bg-[#F1EBDD] border border-[#DED5C2] text-[#3D4A42] text-sm font-semibold flex items-center justify-center gap-2 hover:bg-[#F1EBDD] transition-colors"
          data-testid="view-summary-btn">
          <BarChart3 size={16} className="text-[#103B31]" /> Ver Resumen en Vivo
        </button>
      </div>
    </div>
  );
}

// Player Score Card (dark, big numbers)
function PlayerScoreCard({ player, score, par, onAdjustScore, onUpdateScore, getScoreColor }) {
  const grossScore = score?.gross_score || par;
  const selectedUnit = UNIT_TYPES.find(u => u.value === score?.unit_type);
  const isOtro = score?.unit_type === "otro";

  const handleUnitTypeChange = (value) => {
    const unitType = UNIT_TYPES.find(u => u.value === value);
    onUpdateScore("unit_type", value);
    if (unitType && value !== "otro") {
      onUpdateScore("unit_count", unitType.defaultValue);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-[#DED5C2] shadow-sm px-4 py-3" data-testid={`player-score-${player.player_id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#102E32] truncate">{player.name}</p>
          <p className="text-[10px] text-[#9AA69C]">HCP {player.handicap}</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onAdjustScore(-1)}
            className="w-10 h-10 rounded-lg bg-[#F1EBDD] text-[#102E32] font-bold text-lg flex items-center justify-center active:bg-[#E9E1CE]"
            data-testid={`minus-btn-${player.player_id}`}>
            -
          </button>
          <span className={`w-14 h-10 flex items-center justify-center text-2xl font-black ${getScoreColor(grossScore, par)}`}>
            {grossScore}
          </span>
          <button onClick={() => onAdjustScore(1)}
            className="w-10 h-10 rounded-lg bg-[#F1EBDD] text-[#102E32] font-bold text-lg flex items-center justify-center active:bg-[#E9E1CE]"
            data-testid={`plus-btn-${player.player_id}`}>
            +
          </button>
        </div>
      </div>

      {/* Units */}
      <div className="flex items-center gap-2 pt-3 mt-3 border-t border-[#DED5C2] flex-wrap">
        <div className="flex items-center gap-2">
          <Checkbox
            id={`unit-${player.player_id}`}
            checked={score?.has_unit || false}
            onCheckedChange={(checked) => {
              onUpdateScore("has_unit", checked);
              if (!checked) {
                onUpdateScore("unit_type", "");
                onUpdateScore("unit_count", 1);
              }
            }}
            className="border-[#C4BFAF] data-[state=checked]:bg-[#103B31] data-[state=checked]:border-[#103B31] data-[state=checked]:text-white"
            data-testid={`unit-checkbox-${player.player_id}`}
          />
          <label htmlFor={`unit-${player.player_id}`} className="text-sm text-[#6B7A6F]">Unidad</label>
        </div>

        {score?.has_unit && (
          <>
            <Select value={score?.unit_type || ""} onValueChange={handleUnitTypeChange}>
              <SelectTrigger className="w-36 h-8 bg-white border-[#DED5C2] text-[#102E32] text-xs" data-testid={`unit-type-${player.player_id}`}>
                <SelectValue placeholder="Seleccionar..." />
              </SelectTrigger>
              <SelectContent className="bg-white text-[#102E32] border-[#DED5C2]">
                {UNIT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center justify-between w-full gap-2">
                      <span>{type.label}</span>
                      <span className="text-xs bg-[#103B31]/10 text-[#103B31] px-1.5 py-0.5 rounded font-semibold">
                        {type.defaultValue}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {score?.unit_type && (
              isOtro ? (
                <input type="number" min="1" max="10"
                  value={score?.unit_count || 1}
                  onChange={(e) => onUpdateScore("unit_count", parseInt(e.target.value) || 1)}
                  className="w-14 h-8 bg-white border border-[#DED5C2] rounded-lg text-center text-sm font-bold text-[#102E32] focus:outline-none focus:ring-1 focus:ring-[#103B31]"
                  placeholder="Pts"
                  data-testid={`unit-count-${player.player_id}`} />
              ) : (
                <span className="flex items-center gap-1 bg-[#103B31]/10 text-[#103B31] px-2 py-1 rounded-full text-xs font-bold">
                  {score?.unit_count || selectedUnit?.defaultValue} pts
                </span>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Oyes Preview - Shows who wins in each matchup (supports 2 or 3 secondaries)
function OyesPreview({ players, firstPlace, secondPlace }) {
  const secondaryPlayers = players.filter(p => !p.is_base);

  const firstPlayer = players.find(p => p.player_id === firstPlace);
  const secondPlayer = players.find(p => p.player_id === secondPlace);
  if (!firstPlayer || !secondPlayer) return null;

  const firstIsBase = firstPlayer.is_base;
  const secondIsBase = secondPlayer.is_base;

  const getMatchupResult = (secPair) => {
    if (firstIsBase) {
      return { winner: "base", reason: `${firstPlayer.name.split(' ')[0]} (1°)` };
    }
    if (!firstIsBase && !secondIsBase) {
      return { winner: "secondary", reason: `${firstPlayer.name.split(' ')[0]} y ${secondPlayer.name.split(' ')[0]}` };
    }
    const secIds = secPair.map(p => p.player_id);
    if (secIds.includes(firstPlace)) {
      return { winner: "secondary", reason: `${firstPlayer.name.split(' ')[0]} (1°)` };
    }
    return { winner: "base", reason: `${firstPlayer.name.split(' ')[0]} no participa` };
  };

  const secCombinations = secondaryPlayers.length >= 3
    ? [[0, 1], [0, 2], [1, 2]].map(([a, b]) => [secondaryPlayers[a], secondaryPlayers[b]])
    : [[secondaryPlayers[0], secondaryPlayers[1]]];

  return (
    <div className="space-y-2">
      <p className="text-[10px] uppercase tracking-wider font-bold text-[#9AA69C]">Resultado del Oyes:</p>
      {secCombinations.map((secPair) => {
        const result = getMatchupResult(secPair);
        const isBaseWin = result.winner === "base";
        return (
          <div key={`oyes-${secPair.map(p => p.player_id).join('-')}`} className="flex items-center justify-between text-xs">
            <span className="text-[#8A968C]">vs {secPair.map(p => p.name.split(' ')[0]).join('+')}</span>
            <span className={`font-bold ${isBaseWin ? 'text-[#103B31]' : 'text-[#E6B733]'}`}>
              {isBaseWin ? 'Base +1' : 'Sec +1'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
